import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { toast } from 'dashboard/helper/toast';
import { visionClient } from './visionClient';
import { VISION_EVENT } from './visionEvents';

// Estado reativo + orquestração do turn do Vision.
//
// Não usa Vuex: o Vision é efêmero (sem persistência no Rails). As mensagens vivem
// num `ref` local, no shape que os subcomponentes do Copilot já esperam:
//   user               → { message: { content } }
//   assistant_thinking → { message: { content } }
//   assistant          → { message: { content } }
//
// Agnóstico de provedor: o evento tool_call já chega com `label` (display_label
// do registry, via config por conta no orchestrator) — nada de mapa hardcoded
// de tools aqui.

const formatArgs = args => {
  if (!args || Object.keys(args).length === 0) return '';
  return Object.entries(args)
    .map(([key, value]) => `${key}: ${value}`)
    .join(', ');
};

const formatToolResult = ({ summary }) => summary;

const generateSessionId = () =>
  window.crypto?.randomUUID?.() || `vis_${Date.now().toString(36)}`;

export function useVisionSession() {
  const { t } = useI18n();
  const messages = ref([]);
  const isStreaming = ref(false);

  const formatToolCall = ({ label, args }) => {
    const text = t('VISION.CALLING_TOOL', { label });
    const detail = formatArgs(args);
    return detail ? `${text} (${detail})` : text;
  };

  let unsubscribe = null;
  let sessionId = null;
  let activeTurnId = null;
  let streamingId = null;
  let seq = 0;
  let pendingTurn = null; // resolve do askAndWait do turn ativo
  let pendingBoletoUrl = null; // capturado do tool_result, anexado ao done
  let pendingPixCode = null; // capturado do tool_result, anexado ao done

  const nextId = () => {
    seq += 1;
    return seq;
  };

  const pushMessage = message => {
    messages.value = [...messages.value, message];
  };

  const patchMessage = (id, updater) => {
    messages.value = messages.value.map(m => (m.id === id ? updater(m) : m));
  };

  const removeStreamingMessage = () => {
    if (!streamingId) return;
    messages.value = messages.value.filter(m => m.id !== streamingId);
    streamingId = null;
  };

  const appendToken = text => {
    if (pendingBoletoUrl || pendingPixCode) return;
    if (!streamingId) {
      streamingId = nextId();
      pushMessage({
        id: streamingId,
        message_type: 'assistant',
        message: { content: text },
      });
      return;
    }
    patchMessage(streamingId, m => ({
      ...m,
      message: { ...m.message, content: m.message.content + text },
    }));
  };

  const finalize = reply => {
    let resolved = reply;
    const boletoUrl = pendingBoletoUrl;
    const pixCode = pendingPixCode;
    const hasAsset = boletoUrl || pixCode;
    const displayContent = hasAsset ? '' : reply;
    pendingBoletoUrl = null;
    pendingPixCode = null;

    if (streamingId) {
      patchMessage(streamingId, m => {
        resolved = reply ?? m.message.content;
        return {
          ...m,
          message: {
            content: hasAsset ? '' : resolved,
            ...(boletoUrl ? { boleto_url: boletoUrl } : {}),
            ...(pixCode ? { pix_copia_cola: pixCode } : {}),
          },
        };
      });
    } else if (reply || hasAsset) {
      pushMessage({
        id: nextId(),
        message_type: 'assistant',
        message: {
          content: displayContent,
          ...(boletoUrl ? { boleto_url: boletoUrl } : {}),
          ...(pixCode ? { pix_copia_cola: pixCode } : {}),
        },
      });
    }
    isStreaming.value = false;
    streamingId = null;
    if (pendingTurn) {
      pendingTurn.resolve(resolved ?? '');
      pendingTurn = null;
    }
  };

  // Falha visível do turn: para o streaming, descarta a bolha parcial e avisa — em
  // vez de pendurar o painel esperando um `done` que não vem (timeout do servidor,
  // stream interrompido, ou falha ao abrir o stream).
  const failTurn = () => {
    removeStreamingMessage();
    isStreaming.value = false;
    streamingId = null;
    toast.error(t('VISION.STREAM_ERROR'));
    if (pendingTurn) {
      pendingTurn.resolve('');
      pendingTurn = null;
    }
  };

  const applyEvent = evt => {
    // A sessão é reusável e o canal account_{id} é compartilhado: ignora eventos
    // de um turn que não é o ativo (supersede). Eventos sem turn_id (mock) passam.
    if (evt.turn_id && evt.turn_id !== activeTurnId) return;

    switch (evt.type) {
      case VISION_EVENT.THINKING:
        pushMessage({
          id: nextId(),
          message_type: 'assistant_thinking',
          message: { content: evt.text },
        });
        break;
      case VISION_EVENT.TOOL_CALL:
        pushMessage({
          id: nextId(),
          message_type: 'assistant_thinking',
          message: { content: formatToolCall(evt) },
        });
        break;
      case VISION_EVENT.TOOL_RESULT:
        // Assets keyados pelo CAMPO do evento, não pelo nome da tool — qualquer
        // integração que devolva boleto_url/pix_copia_cola aciona os cards.
        if (evt.boleto_url) {
          pendingBoletoUrl = evt.boleto_url;
          removeStreamingMessage();
        }
        if (evt.pix_copia_cola) {
          pendingPixCode = evt.pix_copia_cola;
          removeStreamingMessage();
        }
        pushMessage({
          id: nextId(),
          message_type: 'assistant_thinking',
          message: { content: formatToolResult(evt) },
        });
        break;
      case VISION_EVENT.TOKEN:
        appendToken(evt.text);
        break;
      case VISION_EVENT.DONE:
        finalize(evt.reply);
        break;
      case VISION_EVENT.ERROR:
        failTurn();
        break;
      default:
        break;
    }
  };

  const ask = async (query, context) => {
    if (pendingTurn) {
      pendingTurn.resolve('');
      pendingTurn = null;
    }
    if (unsubscribe) unsubscribe();
    streamingId = null;
    pendingBoletoUrl = null;
    pendingPixCode = null;
    sessionId = sessionId ?? generateSessionId();
    activeTurnId = generateSessionId();

    pushMessage({
      id: nextId(),
      message_type: 'user',
      message: { content: query },
    });
    isStreaming.value = true;

    try {
      unsubscribe = await visionClient.query(
        { session_id: sessionId, turn_id: activeTurnId, query, context },
        applyEvent
      );
    } catch {
      // stream morto antes do handshake (createSseStream rejeita) — falha visível
      // em vez de deixar o spinner travado.
      failTurn();
    }
  };

  // Igual ao ask(), mas retorna uma Promise que resolve com o texto final da resposta
  // (evento `done`). Usado pela ponte do modo voz Realtime: a tool delegadora `consultar`
  // espera essa Promise e devolve o texto pra OpenAI falar.
  const askAndWait = (query, context) => {
    ask(query, context); // roda a parte síncrona (settla turn pendente anterior)
    return new Promise(resolve => {
      pendingTurn = { resolve };
    });
  };

  const reset = () => {
    if (pendingTurn) {
      pendingTurn.resolve('');
      pendingTurn = null;
    }
    if (unsubscribe) unsubscribe();
    unsubscribe = null;
    sessionId = null;
    activeTurnId = null;
    streamingId = null;
    pendingBoletoUrl = null;
    pendingPixCode = null;
    messages.value = [];
    isStreaming.value = false;
  };

  return { messages, isStreaming, ask, askAndWait, reset };
}
